import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

const ElectronicSignature = ({ onClose, onConfirm }) => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [agreed, setAgreed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [userName, setUserName] = useState("의뢰인");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [userData, setUserData] = useState(null);
  const [successFeePercentage, setSuccessFeePercentage] = useState(10);
  const [lawsuitAmount, setLawsuitAmount] = useState(5000);

  // 페이지 로드 시 모달이 하단에서 올라오도록 설정
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // 성공보수 퍼센티지와 소송 금액 가져오기
        const feePercentage = Cookies.get("lawsuit_success_fee_percentage");
        if (feePercentage) {
          setSuccessFeePercentage(parseInt(feePercentage, 10));
        }

        const amount = Cookies.get("lawsuit_amount");
        if (amount) {
          setLawsuitAmount(parseInt(amount, 10));
        }

        const token = Cookies.get(
          API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN
        );

        if (!token) {
          throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
        }

        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("사용자 정보를 가져오는데 실패했습니다.");
        }

        const data = await response.json();
        setUserData(data);
      } catch (err) {
        setError(err.message || "사용자 정보를 가져오는데 실패했습니다.");
      } finally {
        setVisible(true);
      }
    };

    fetchUserData();
  }, []);

  // 모달 닫기 처리
  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  // 동의 처리
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  const modalVariants = {
    hidden: {
      y: "100%",
      opacity: 1,
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
      },
    },
    exit: {
      y: "100%",
      opacity: 1,
      transition: {
        duration: 0.3,
      },
    },
  };

  return (
    <ModalOverlay onClick={handleClose}>
      <AnimatePresence>
        {visible && (
          <ModalContainer
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>
                SK텔레콤 개인정보 유출 손해배상청구 위임장
              </ModalTitle>
              <CloseButton onClick={handleClose}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              <AgreementTitle>
                <strong>소송위임계약서</strong>
              </AgreementTitle>

              <AgreementParties>
                <div>위임인(갑): {userData?.name || "의뢰인"}</div>
                <div>
                  수임인(을): 법무법인 더 에이치 황해, 대표변호사 천상현
                </div>
                <div>사건명: SK텔레콤 개인정보 유출 손해배상청구</div>
              </AgreementParties>

              <SectionContainer>
                <Section>
                  <SectionTitle>제1조(권한의 수여)</SectionTitle>
                  <SectionContent>
                    "갑"은 위 소송대리사무를 처리하기 위하여 별도 작성된
                    소송위임장에 기재된 권한을 "을"에게 수여한다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제2조(착수금)</SectionTitle>
                  <SectionContent>
                    "갑"은 위 사건의 착수금(인지대·송달료 등 소송진행에 필요한
                    비용 포함)으로 금 {lawsuitAmount.toLocaleString()} 원(￦
                    {lawsuitAmount.toLocaleString()})을 "을"에게 지급한다. 본
                    계약 체결 이후 위임계약 해지 등 어떠한 사유로도 착수금의
                    반환을 청구할 수 없다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제3조(비용)</SectionTitle>
                  <SectionContent>
                    위 착수금 외에 위임사무를 처리하는 추가비용은 없는 것으로
                    한다. 다만, "갑"이 "을"과의 협의에 따르지 않고 제1심에 대한
                    불복항소, 상고심의 진행을 원하는 경우 인지대, 송달료를
                    부담하기로 한다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제4조(성공보수금)</SectionTitle>
                  <SectionContent>
                    위 사건에서 승소(일부 승소 포함)할 경우 "갑"이 승소로 얻은
                    경제적 이익가액(판결금ㆍ조정금ㆍ합의금ㆍ보상금 포함)을
                    기준으로 그 {successFeePercentage}%(부가가치세 별도)를
                    성공보수금으로 정하며, "갑"이 손해배상금을 수령하는 당일
                    "을"에게 지급한다 (보상금은 SKT측이 손해배상책임 여하와
                    관계없이 보상 차원에서 무상 제공하는 일체의 경제적 이익을
                    가리킴).이 때 '합의금'에는 소송절차의 내외를 불문하고
                    이루어진 보상 협의에 따른 일체의 보상금도 포함되는 것으로
                    한다.성공보수금 지급이 지체될 경우 연 12%의 지연손해금을
                    지급한다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제5조(승소로 보는 경우)</SectionTitle>
                  <SectionContent>
                    다음의 경우에는 승소한 것으로 보고 성공보수금을 지급하여야
                    한다.
                    <br />① "갑"이 "을"의 승낙을 받지 않고 임의로 상대방과
                    합의를 한 경우
                    <br />② "갑"이 "을"의 승낙 없이 임의로 소송을 취하하거나
                    인낙 또는 화해를 한 경우
                    <br />③ "갑"이 이 약정에 정한 의무를 이행하지 않는 등 "갑"의
                    책임 있는 사유로 인해 "을"이 본 사건을 수행할 수 없어 사임한
                    경우
                    <br />④ "갑"이 본 계약을 일방적으로 해제/해지하거나, "갑"의
                    귀책사유로 인해 계약이 해지된 경우
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제6조(계약의 해제)</SectionTitle>
                  <SectionContent>
                    "갑"이 사건의 내용을 허위로 진술하거나 본 계약에서 정한
                    의무를 이행하지 않을 경우, "을"은 본 계약을 해지하고 소송
                    수행을 중단할 수 있다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제7조(관할에 대한 합의)</SectionTitle>
                  <SectionContent>
                    본 계약과 관련한 일체의 분쟁에 대해서는 인천지방법원을
                    관할법원으로 한다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제8조(인장의 조각 및 사용)</SectionTitle>
                  <SectionContent>
                    "갑"은 본 계약에 의하여 소장 제출 및 판결금 등 수령에 필요할
                    경우 "을"이 "갑"의 인장을 조각하여 사용하는 것에 동의한다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제9조(개인정보 수집·이용 동의)</SectionTitle>
                  <SectionContent>
                    1. 수집 항목: 성명, 생년월일, 주소, 연락처, 이메일, 피해
                    내용 및 관련 증빙자료
                    <br />
                    <br />
                    2. 수집 목적: 소송 수행 및 관련 법률 서비스 제공, 의뢰인과의
                    연락 및 소송 진행 상황 안내
                    <br />
                    <br />
                    3. 보유 기간: 소송 종료 후 관련 법령에 따른 보존 기간 동안
                    보관 (통상 소송 종료 후 5년)
                    <br />
                    <br />
                    4. "갑"은 위와 같이 개인정보를 수집·이용하는 데 동의하며,
                    "을"은 수집된 개인정보를 소송 목적 외에는 사용하지 않을 것을
                    확약한다. 다만 법원, 에스케이텔레콤 주식회사(피고), 소송
                    관련 전문가 및 유관 기관에 제공하는 것은 소송 수행을 위한
                    필요한 범위 내에서 허용된다.
                  </SectionContent>
                </Section>

                <SignatureDate>
                  {new Date().toLocaleDateString("ko-KR", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </SignatureDate>

                <CheckboxContainer onClick={() => setAgreed(!agreed)}>
                  <CheckboxImage
                    src={
                      agreed ? "/png/checked.png" : "/png/checkedinActive.png"
                    }
                    alt="체크박스"
                  />
                  <CheckboxLabel>
                    위 내용을 모두 확인하고 동의합니다
                  </CheckboxLabel>
                </CheckboxContainer>
              </SectionContainer>
            </ModalBody>
            <ModalFooter>
              <ConfirmButton onClick={handleConfirm} disabled={!agreed}>
                동의하고 계속하기
              </ConfirmButton>
            </ModalFooter>
          </ModalContainer>
        )}
      </AnimatePresence>
    </ModalOverlay>
  );
};

// 스타일 컴포넌트
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 999;
  touch-action: none;

  @media (min-width: 769px) {
    align-items: center;
  }
`;

const ModalContainer = styled(motion.div)`
  width: 100%;
  max-width: 500px;
  background-color: white;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 80vh;

  @media (min-width: 769px) {
    border-radius: 16px;
    max-width: 420px;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  flex: 1;
  text-align: center;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  padding: 0;
  margin: 0;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  font-size: 14px;
  line-height: 1.6;
`;

const ModalFooter = styled.div`
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: center;
`;

const ConfirmButton = styled.button`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0 14px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: ${(props) => (props.disabled ? "#E5E5E5" : "#0075ff")};
  color: ${(props) => (props.disabled ? "#999" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  border: none;
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
`;

const AgreementTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  text-align: center;
`;

const AgreementParties = styled.div`
  margin-bottom: 20px;
  line-height: 1.6;

  div {
    margin-bottom: 8px;
  }
`;

const SectionContainer = styled.div`
  margin-bottom: 20px;
`;

const Section = styled.div`
  margin-bottom: 15px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
`;

const SectionContent = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #444;
  white-space: pre-line;
  margin-bottom: 15px;
`;

const SignatureDate = styled.div`
  margin: 20px 0;
  text-align: center;
  font-weight: 500;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 15px;
  cursor: pointer;
`;

const CheckboxImage = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 10px;
`;

const CheckboxLabel = styled.div`
  font-size: 15px;
  font-weight: 500;
`;

export default ElectronicSignature;
